import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import HomeIcon from '@mui/icons-material/Home';

import { AppProvider, Router } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';

import { useAuth } from 'contexts/AuthContext';
import { NoticeCountProvider } from 'contexts/NoticeCountContext';

import AppTitle from 'components/layout/AppTitle';
import ToolbarAccount from 'components/layout/ToolbarAccount';
import { navigationItems } from 'layouts/navigation/index';
import {
  NoticeToolbarBadge,
  SidebarFooter,
  dashboardTheme,
  handleAddAccount,
  handleSwitchAccount,
  signInOut,
  toSession,
} from 'layouts/router/common';

const IndexLayout: React.FC = () => {
  const { userSession } = useAuth();
  const navigations = navigationItems();

  const router = useRouter('/');

  const authentication = React.useMemo(() => signInOut, []);

  const ToolbarAccountSlot = React.useCallback(
    () => (
      <ToolbarAccount
        userSession={userSession}
        onSignOut={signInOut.signOut}
        onSwitchAccount={handleSwitchAccount}
        onAddAccount={handleAddAccount}
      />
    ),
    [userSession],
  );

  const ToolbarActionsSlot = React.useCallback(
    () => (
      <NoticeToolbarBadge
        allowedScreens={userSession.allowedScreens}
        userName={userSession.userName}
      />
    ),
    [userSession.allowedScreens, userSession.userName],
  );

  return (
    <>
      <NoticeCountProvider>
        <AppProvider
          session={toSession(userSession)}
          authentication={authentication}
          navigation={navigations}
          router={router}
          theme={dashboardTheme}
        >
          <DashboardLayout
            hideNavigation
            slots={{
              appTitle: CustomAppTitle,
              sidebarFooter: SidebarFooter,
              toolbarActions: ToolbarActionsSlot,
              toolbarAccount: ToolbarAccountSlot,
            }}
          >
            <PageContainer maxWidth="xl">
              <Outlet />
            </PageContainer>
          </DashboardLayout>
        </AppProvider>
      </NoticeCountProvider>
    </>
  );
};

function useRouter(initialPath: string): Router {
  const navigate = useNavigate();
  const [pathname, setPathname] = React.useState(initialPath);

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path: string | URL) => {
        navigate(String(path));
        setPathname(String(path));
      },
    };
  }, [pathname, navigate]);

  return router;
}

export function CustomAppTitle() {
  return <AppTitle icon={<HomeIcon />} title="Ardito Portal - Kanzhong -" color="#37474f" />;
}
export default IndexLayout;
